import { api } from "./api";

export type AccountType =
  | "GIROKONTO"
  | "SPARKONTO"
  | "TAGESGELD"
  | "FESTGELD"
  | "DEPOT"
  | "KRYPTO"
  | "P2P"
  | "KREDIT"
  | "KREDITKARTE"
  | "BAUSPAR"
  | "VERSICHERUNG"
  | "SONSTIGE";

export type LiquidityCategory = "LIQUID" | "ANLAGE" | "VERBINDLICHKEIT";
export type Sector = "HAUSHALT" | "IMMOBILIE" | "UNZUGEORDNET";
export type BalanceSource = "INITIAL" | "MANUAL" | "IMPORT" | "DERIVED";

export interface BalanceDto {
  id: string;
  date: string;
  balanceCents: number;
  source: BalanceSource;
  note: string | null;
  createdAt: string;
}

export interface AccountListItem {
  id: string;
  name: string;
  type: AccountType;
  institute: string | null;
  currency: string;
  liquidityCategory: LiquidityCategory;
  sector: Sector;
  isActive: boolean;
  notes: string | null;
  /** Bei true zieht /budget den Ist-Wert aus Transactions, sonst aus CashflowActual. */
  usesImportedTransactions: boolean;
  latestBalance: { date: string; balanceCents: number } | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountDetail extends AccountListItem {
  balances: BalanceDto[];
}

export interface AccountSummary {
  byType: { type: AccountType; totalCents: number; count: number }[];
  byLiquidity: Record<LiquidityCategory, { totalCents: number; count: number }>;
  totalActive: number;
  totalCents: number;
}

export interface CreateAccountInput {
  name: string;
  type: AccountType;
  institute?: string;
  liquidityCategory: LiquidityCategory;
  sector?: Sector;
  currency: "EUR";
  notes?: string;
  usesImportedTransactions?: boolean;
  initialBalanceCents: number;
  initialBalanceDate: string;
}

export type UpdateAccountInput = Partial<{
  name: string;
  type: AccountType;
  institute: string | null;
  liquidityCategory: LiquidityCategory;
  sector: Sector;
  notes: string | null;
  usesImportedTransactions: boolean;
  isActive: boolean;
}>;

export interface CreateBalanceInput {
  date: string;
  balanceCents: number;
  source?: BalanceSource;
  note?: string;
}

export interface ListAccountsFilters {
  active?: "true" | "false";
  sector?: Sector;
  liquidityCategory?: LiquidityCategory;
}

function qs(filters: ListAccountsFilters): string {
  const entries = Object.entries(filters).filter(([, v]) => v !== undefined) as [string, string][];
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries).toString()}`;
}

export const accountsApi = {
  list: (filters: ListAccountsFilters = {}) =>
    api<{ accounts: AccountListItem[] }>(`/accounts${qs(filters)}`),
  summary: () => api<AccountSummary>("/accounts/summary"),
  get: (id: string) => api<AccountDetail>(`/accounts/${id}`),
  create: (input: CreateAccountInput) =>
    api<AccountListItem>("/accounts", { method: "POST", body: input }),
  update: (id: string, input: UpdateAccountInput) =>
    api<AccountListItem>(`/accounts/${id}`, { method: "PATCH", body: input }),
  softDelete: (id: string) => api<void>(`/accounts/${id}`, { method: "DELETE" }),
  hardDelete: (id: string) => api<void>(`/accounts/${id}?force=true`, { method: "DELETE" }),
  listBalances: (id: string, limit = 50, offset = 0) =>
    api<{ balances: BalanceDto[] }>(`/accounts/${id}/balances?limit=${limit}&offset=${offset}`),
  addBalance: (id: string, input: CreateBalanceInput) =>
    api<BalanceDto>(`/accounts/${id}/balances`, { method: "POST", body: input }),
  deleteBalance: (id: string, balanceId: string) =>
    api<void>(`/accounts/${id}/balances/${balanceId}`, { method: "DELETE" }),
};
